import React, { useCallback } from "react";
import FontAwesomeIcon from "@fortawesome/react-fontawesome";
import { faArrowUp, faArrowDown, faPlus } from "@fortawesome/fontawesome-free-solid/";
import i18next from "i18next";

import { TextInput } from "../TextInput";

import config from "config";

const SelectionStep = ({ step, readOnly, onStepUpdate, onCorrectiveAction }) => {
    const toggleSelectionType = useCallback(() => {
        if (!readOnly) {
            onStepUpdate({ ...step, maxCount: step.maxCount === 1 ? 0 : 1 });
        }
    }, [step, onStepUpdate]);

    const addSelectionOption = useCallback(() => {
        let options = [...step.checklistTaskSelectionOptions];
        options.push({ id: options.length, name: "", selectionOrder: options.length });
        onStepUpdate({ ...step, checklistTaskSelectionOptions: options });
    }, [step, onStepUpdate]);

    const removeSelectionOption = useCallback((index) => {
        let options = [...step.checklistTaskSelectionOptions];
        options.splice(index, 1);
        onStepUpdate({ ...step, checklistTaskSelectionOptions: options });
    }, [step, onStepUpdate]);

    const updateSelectionOption = useCallback((index, name) => {
        let checklistTaskSelectionOptions = [...step.checklistTaskSelectionOptions];

        let selection = checklistTaskSelectionOptions[index];
        selection.name = name;

        if (selection.correctiveAction) {
            selection.correctiveAction.name = `${step.name} - ${selection.name}`;
        }

        checklistTaskSelectionOptions[index] = {
            ...selection,
        };

        onStepUpdate({ ...step, checklistTaskSelectionOptions: checklistTaskSelectionOptions });
    }, [step, onStepUpdate]);

    const moveSelectionOption = useCallback((index, direction) => {
        if ((direction === "up" && index === 0) || (direction === "down" && index === step.checklistTaskSelectionOptions.length - 1)) {
            return;
        }

        let checklistTaskSelectionOptions = [...step.checklistTaskSelectionOptions];
        let indexToSwap = direction === "up" ? index - 1 : index + 1;
        let selection = checklistTaskSelectionOptions[index];
        let selectionToSwap = checklistTaskSelectionOptions[indexToSwap];
        let existingOrder = selection.selectionOrder;
        selection.selectionOrder = selectionToSwap.selectionOrder;
        selectionToSwap.selectionOrder = existingOrder;

        onStepUpdate({ ...step, checklistTaskSelectionOptions: checklistTaskSelectionOptions.sort((a, b) => a.selectionOrder - b.selectionOrder) });
    },[step, onStepUpdate]);

    const removeSelectionCorrectiveAction = useCallback((index) => {
        let checklistTaskSelectionOptions = [...step.checklistTaskSelectionOptions];
        checklistTaskSelectionOptions[index] = {
            ...checklistTaskSelectionOptions[index],
            correctiveAction: null
        };
        onStepUpdate({ ...step, checklistTaskSelectionOptions: checklistTaskSelectionOptions });
    }, [step, onStepUpdate]);

    const setCorrectiveAction = useCallback((selection, index) => {
        let correctiveAction = selection.correctiveAction;
        if (correctiveAction == null) {
            correctiveAction = {
                name: `${step.name} - ${selection.name}`,
                correctiveAction: true,
                checklistTaskOptionConfigs: []
            };
            onStepUpdate({ ...step, correctiveAction: correctiveAction });
        }

        let stepsSorted = [...correctiveAction.checklistTaskOptionConfigs];
        stepsSorted.sort((a, b) => { return a.optionOrder - b.optionOrder; });

        var stepIndex = 0;
        onCorrectiveAction({
            correctiveAction: {
                ...correctiveAction,
                checklistTaskOptionConfigs: stepsSorted.map((step) => ({ ...step, uid: stepIndex++ })),
            },
            ownerType: "selection",
            ownerName: selection.name,
            ownerIndex: index,
            ownerStepUpdateFunction: (updatedCorrectiveAction) => {
                let checklistTaskSelectionOptions = [...step.checklistTaskSelectionOptions];
                checklistTaskSelectionOptions[index] = {
                    ...checklistTaskSelectionOptions[index],
                    correctiveAction: updatedCorrectiveAction,
                };
                return { ...step, checklistTaskSelectionOptions: checklistTaskSelectionOptions };
            },
        });
    }, [step, onStepUpdate, onCorrectiveAction]);

    return (
        step.checklistTaskOptionType.token === "selection" && (
            <div>
                <label>{i18next.t("form-labels.type-of-selection")}</label>
                <div className="form-group">
                    <label>
                        <input type="radio" checked={step.maxCount === 1} onChange={toggleSelectionType} disabled={readOnly}></input> {i18next.t("form-labels.single")}
                    </label>
                    <label>
                        <input type="radio" checked={step.maxCount !== 1} onChange={toggleSelectionType} disabled={readOnly}></input> {i18next.t("form-labels.multiple")}
                    </label>
                </div>

                <div className="row" style={{ backgroundColor: "#f1f1f1" }}>
                    <div className="checklist-padding">
                        <div>
                            <label>{i18next.t("form-labels.selection-descriptions")}:</label>
                            {step.checklistTaskSelectionOptions.map((selection, index) => {
                                const addViewCorrectiveAction = selection.correctiveAction ? i18next.t("pages.checklists.view-corrective-action") : 
                                    (!readOnly ? i18next.t("pages.checklists.add-corrective-action") : "");

                                return (
                                    <div key={index} className="row checklist-step-selection">
                                        <div className="col-sm-1" style={{ padding: "15px 0px 0px 30px" }}>
                                            {index + 1}.
                                        </div>
                                        <div className="col-sm-9" style={{ padding: "0px 4px 8px 8px" }}>
                                            <TextInput
                                                input={{
                                                    value: selection.name ? selection.name : "",
                                                    onChange: (event) => updateSelectionOption(index, event.target.value),
                                                }}
                                                type="text"
                                                label=""
                                                readOnly={readOnly}
                                                meta={selection.errors && selection.errors.name ? { touched: true, error: step.errors.name } : {}}
                                            />
                                            {step.name && selection.name && onCorrectiveAction && (
                                                <div className="pull-right" style={{ fontSize: "14px" }}>
                                                    <span>
                                                        <a onClick={() => { setCorrectiveAction(selection, index); }}>{addViewCorrectiveAction}</a>
                                                    </span>
                                                    {selection.correctiveAction && !readOnly && (
                                                        <span>
                                                            {" "}
                                                            | <a onClick={() => removeSelectionCorrectiveAction(index)}>{i18next.t("pages.checklists.remove-corrective-action")}</a>
                                                        </span>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                        {!readOnly && (
                                            <div className="col-sm-2" style={{ padding: "5px 0px", display: "inline-flex" }}>
                                                <div>
                                                    <div style={{ height: "24px" }}>
                                                        {index !== 0 && (
                                                            <button className="btn btn-basic" onClick={() => moveSelectionOption(index, "up")}>
                                                                <FontAwesomeIcon icon={faArrowUp} />
                                                            </button>
                                                        )}
                                                    </div>
                                                    <div>
                                                        {index !== step.checklistTaskSelectionOptions.length - 1 && (
                                                            <button className="btn btn-basic" onClick={() => moveSelectionOption(index, "down")}>
                                                                <FontAwesomeIcon icon={faArrowDown} />
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>
                                                <div style={{ padding: "5px 0px 0px 10px" }}>
                                                    <img
                                                        src={`${config.WEB_BASE_URL}/images/icon/delete-trash-can.svg`}
                                                        name="delete"
                                                        alt={i18next.t("delete")}
                                                        onClick={() => removeSelectionOption(index)}
                                                    />
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                            <hr />
                            {!readOnly && (
                                <button className="btn btn-default" onClick={addSelectionOption}>
                                    <FontAwesomeIcon icon={faPlus} /> {i18next.t("pages.checklists.selection")}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        )
    );
};

export default SelectionStep;
